import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import api from "../../services/api";
import Loader from "../../components/Loader";
import Table from "../../components/Table";
import Pagination from "../../components/Pagination";

const HEADER = [
  { title: "#", key: "row_number" },
  { title: "Demandeur", key: "user_from_first_name" },
  { title: "Contacté", key: "user_to_first_name" },
  { title: "Entreprise", key: "company_name" },
  { title: "Type", key: "company_annonce_type" },
  { title: "Titre", key: "company_annonce_title" },
  { title: "NDA", key: "nda_user_from_signed" },
  { title: "NDA signé le", key: "nda_user_from_signed_at" },
  { title: "Créé", key: "created_at" },
  { title: "MAJ", key: "updated_at" },
  { title: "Statut", key: "status" },
  { title: "Actions", key: "actions" },
];

const List = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "",
    company_annonce_type: searchParams.get("company_annonce_type") || "",
    nda_user_from_signed: searchParams.get("nda_user_from_signed") || "",
    nda_user_to_signed: searchParams.get("nda_user_to_signed") || "",
    page: Number(searchParams.get("page")) || 1,
    limit: Number(searchParams.get("limit")) || 50,
    sort: { created_at: -1 },
  });

  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      let query = {};
      if (filters.status) query.status = filters.status;
      if (filters.company_annonce_type) query.company_annonce_type = filters.company_annonce_type;
      if (filters.nda_user_from_signed) query.nda_user_from_signed = filters.nda_user_from_signed === "true";
      if (filters.page) query.skip = (filters.page - 1) * filters.limit;
      if (filters.limit) query.limit = filters.limit;
      if (filters.sort) query.sort = filters.sort;

      setSearchParams(filters);

      const { ok, data, total } = await api.post("/nda/search", query);
      if (!ok) throw data;
      setDiscussions(data);
      setTotal(total);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la récupération des données");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const formatAnnonceType = (type) => {
    switch (type) {
      case "levee":
        return "Levée de fonds";
      case "cession":
        return "Cession";
      default:
        return type || "-";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const NDAStatus = ({ signed }) => (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${signed ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
      {signed ? "✓" : "✗"}
    </span>
  );

  const handleAccept = async (id) => {
    const res = await api.put(`/nda/${id}`, { status: "ACCEPTED" });
    if (res.ok) {
      toast.success("NDA accepté");
      fetchData();
    }
  };

  const handleRefuse = async (id) => {
    const res = await api.put(`/nda/${id}`, { status: "REFUSED" });
    if (res.ok) {
      toast.success("NDA refusé");
      fetchData();
    }
  };

  return (
    <div className="p-4 lg:p-8">
      <header className="w-full">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-semibold text-black mb-2">Discussions</h1>
            <p className="text-sm lg:text-base text-gray-500">Voici la liste des discussions</p>
          </div>
          <div className="bg-primary text-white px-3 py-2 rounded-lg">
            <span className="text-sm font-medium">Total: {total}</span>
          </div>
        </div>
      </header>
      <div className="flex flex-wrap gap-2 sm:gap-4 items-center mt-4">
        <select
          className="block w-full sm:w-fit px-2 py-1.5 text-sm border border-gray-300 bg-white rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary"
          value={filters.status}
          onChange={(event) => setFilters((prevFilters) => ({ ...prevFilters, status: event.target.value, page: 1 }))}>
          <option value="">Tous les statuts</option>
          <option value="PENDING">En attente</option>
          <option value="ACCEPTED">Accepté</option>
          <option value="ONGOING">En cours</option>
          <option value="STOPPED">Arrêté</option>
          <option value="REFUSED">Refusé</option>
        </select>

        <select
          className="block w-full sm:w-fit px-2 py-1.5 text-sm border border-gray-300 bg-white rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary"
          value={filters.company_annonce_type}
          onChange={(event) => setFilters((prevFilters) => ({ ...prevFilters, company_annonce_type: event.target.value, page: 1 }))}>
          <option value="">Tous les types</option>
          <option value="levee">Levée de fonds</option>
          <option value="cession">Cession</option>
        </select>

        <select
          className="block w-full sm:w-fit px-2 py-1.5 text-sm border border-gray-300 bg-white rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary"
          value={filters.nda_user_from_signed}
          onChange={(event) => setFilters((prevFilters) => ({ ...prevFilters, nda_user_from_signed: event.target.value, page: 1 }))}>
          <option value="">NDA - Tous</option>
          <option value="true">NDA - Signé</option>
          <option value="false">NDA - Non signé</option>
        </select>
      </div>
      {loading ? (
        <Loader />
      ) : (
        <section className="w-full py-2 lg:py-4 flex flex-col">
          <div className="overflow-x-auto shadow-sm rounded-lg border border-gray-200">
            <Table
              fixed={true}
              sort={filters.sort}
              onSort={(key) => {
                setFilters({ ...filters, sort: { [key]: filters.sort[key] === 1 ? -1 : 1 } });
              }}
              header={HEADER}
              loading={loading}
              noResultRender={<div className="w-full bg-white text-center p-4 my-5 rounded shadow-sm">Aucun résultat trouvé</div>}>
              {discussions.map((discussion, index) => (
                <tr
                  onClick={() => navigate(`/nda/${discussion._id}`)}
                  className="bg-white odd:bg-gray-50 hover:bg-gray-100 h-12 rounded-lg cursor-pointer border hover:border-gray-400"
                  key={discussion._id}>
                  <td className="p-1 text-center font-medium text-gray-500 w-8 text-xs">{(filters.page - 1) * filters.limit + index + 1}</td>
                  <td className="p-1 w-24 truncate text-sm" title={`${discussion.user_from_first_name} ${discussion.user_from_last_name}`}>
                    {discussion.user_from_first_name} {discussion.user_from_last_name}
                  </td>
                  <td className="p-1 w-24 truncate text-sm" title={`${discussion.user_to_first_name} ${discussion.user_to_last_name}`}>
                    {discussion.user_to_first_name} {discussion.user_to_last_name}
                  </td>
                  <td className="p-1 w-32 truncate text-sm" title={discussion.company_name}>
                    {discussion.company_name || "-"}
                  </td>
                  <td className="p-1 w-20 truncate text-sm">{formatAnnonceType(discussion.company_annonce_type)}</td>
                  <td className="p-1 w-80 truncate text-sm" title={discussion.company_annonce_title}>
                    {discussion.company_annonce_title || "-"}
                  </td>
                  <td className="p-1 text-center w-16">
                    <NDAStatus signed={discussion.nda_user_from_signed} />
                  </td>
                  <td className="p-1 w-20 truncate text-xs">{formatDateTime(discussion.nda_user_from_signed_at)}</td>
                  <td className="p-1 w-20 truncate text-xs">{formatDateTime(discussion.created_at)}</td>
                  <td className="p-1 w-20 truncate text-xs">{formatDateTime(discussion.updated_at)}</td>
                  <td className="p-1 w-20">
                    <span
                      className={`text-xs rounded-md text-center py-1 px-2 ${
                        discussion.status === "PENDING"
                          ? "bg-yellow-300"
                          : discussion.status === "ONGOING"
                            ? "bg-blue-300"
                            : discussion.status === "ACCEPTED"
                              ? "bg-green-300"
                              : "bg-red-300"
                      }`}>
                      {discussion.status}
                    </span>
                  </td>
                  <td className="p-1 w-20">
                    {(discussion.status === "PENDING" || discussion.status === "REFUSED") && (
                      <button
                        className="border border-primary text-primary px-2 py-1 rounded text-xs hover:bg-primary hover:text-white transition"
                        onClick={() => handleAccept(discussion._id)}>
                        Accepter
                      </button>
                    )}
                    {discussion.status === "ACCEPTED" && (
                      <button
                        className="border border-red-500 text-red-500 px-2 py-1 rounded text-xs hover:bg-red-500 hover:text-white transition"
                        onClick={() => handleRefuse(discussion._id)}>
                        Refuser
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        </section>
      )}
      <Pagination page={filters.page} setPage={(page) => setFilters({ ...filters, page })} last={Math.ceil(total / filters.limit)} />
    </div>
  );
};

export default List;
